import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, waLink } from "@/data/content";
import { Reveal, MaskLine, Eyebrow, EASE } from "./Reveal";

const ServiceRow = ({ s, active, onEnter }) => (
  <motion.a
    href={waLink(`Olá! Gostaria de saber mais sobre: ${s.title}.`)}
    target="_blank"
    rel="noreferrer"
    data-testid={`service-item-${s.id}`}
    onMouseEnter={onEnter}
    onFocus={onEnter}
    className="group relative block border-t border-white/10 py-8 md:py-10"
  >
    <span className={`absolute left-0 top-0 h-px bg-[var(--cyan)] transition-[width] duration-700 ${active ? "w-full" : "w-0"}`} />
    <div className="grid grid-cols-12 gap-4 items-start">
      <span className="col-span-2 md:col-span-1 font-mono text-sm text-[var(--cyan)] pt-2">{s.code}</span>
      <div className="col-span-10 md:col-span-10">
        <h3 className={`font-display text-3xl md:text-5xl font-extrabold uppercase leading-[0.95] transition-colors duration-300 ${active ? "text-white" : "text-[var(--steel-2)]"}`}>
          {s.title}
        </h3>
        <motion.div
          initial={false}
          animate={{ height: active ? "auto" : 0, opacity: active ? 1 : 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="overflow-hidden"
        >
          <p className="mt-4 max-w-xl text-[var(--steel-2)] leading-relaxed">{s.text}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {s.tags.map((t) => (
              <span key={t} className="font-mono text-[11px] uppercase tracking-widest border border-white/15 rounded-full px-3 py-1 text-[var(--steel)]">{t}</span>
            ))}
          </div>
          <img src={s.image} alt={s.title} className="lg:hidden mt-6 w-full aspect-[16/10] object-cover rounded-sm" />
        </motion.div>
      </div>
      <ArrowUpRight className={`hidden md:block col-span-1 justify-self-end mt-2 transition-all duration-500 ${active ? "text-[var(--cyan)] rotate-45" : "text-white/30"}`} size={32} />
    </div>
  </motion.a>
);

export const Services = () => {
  const [active, setActive] = useState(0);
  const current = SERVICES[active];

  return (
    <section id="servicos" data-testid="services-section" className="relative bg-[var(--ink)] py-24 md:py-36">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-24">
          <div className="lg:col-span-7">
            <Reveal><Eyebrow testid="services-eyebrow">Serviços · 06 especialidades</Eyebrow></Reveal>
            <h2 className="mt-6 font-display font-black uppercase text-5xl md:text-7xl leading-[0.9]">
              <MaskLine inView>Projetos que</MaskLine>
              <MaskLine inView delay={0.1}><span className="steel-text">funcionam em campo.</span></MaskLine>
            </h2>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5 lg:pt-16">
            <p className="text-base md:text-lg text-[var(--steel-2)] leading-relaxed">
              Da concepção à documentação final, cada projeto é desenvolvido com rigor técnico, foco em segurança
              e eficiência operacional para a sua indústria.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 border-b border-white/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05} y={20}>
                <ServiceRow s={s} active={active === i} onEnter={() => setActive(i)} />
              </Reveal>
            ))}
          </div>

          <div className="hidden lg:block lg:col-span-5">
            <div className="sticky top-28 aspect-[4/5] overflow-hidden rounded-sm border border-white/10 bg-[var(--navy)]" data-testid="services-image-panel">
              {SERVICES.map((s, i) => (
                <motion.img
                  key={s.id}
                  src={s.image}
                  alt={s.title}
                  initial={false}
                  animate={{ opacity: active === i ? 1 : 0, scale: active === i ? 1 : 1.12, clipPath: active === i ? "inset(0% 0% 0% 0%)" : "inset(0% 0% 100% 0%)" }}
                  transition={{ duration: 1, ease: EASE }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-transparent to-transparent" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,transparent_0%,rgba(7,13,31,0.55)_70%)]" />
              <div className="absolute bottom-0 inset-x-0 p-8 flex items-end justify-between">
                <span className="font-display text-8xl font-black text-white/90 leading-none">{current.code}</span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--cyan)] text-right max-w-[50%]">{current.title}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
